import type {ReactElement} from 'react';
import React, {useContext, useEffect, useRef, useState} from 'react';
import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {DisplayType, ProductType, Status} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {Text, TextSize} from '@Components/text';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {InputField} from '@Components/input-field-v2';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import type {InputFieldControlItemData} from '@Components/input-field-v2/input-field.types';
import {InputControlItemType, InputFieldType} from '@Components/input-field-v2/input-field.types';
import {ModalDetails} from '@Components/modal/modal-context';
import {processDesignForPublishing} from '@Panels/poster-publishing-options-panel/publishing-process-design-library';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {setOrderDetails, setProductType, setShowLoader} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {PublishingProgressBar} from '@Panels/poster-publishing-options-panel/components/publishing-progress-bar/publishing-progress-bar';
import {useFreebieProduct, useHashedId, usePrintProduct, useShowLoader} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {PublishingLoaderScreen} from '@Panels/poster-publishing-options-panel/components/publishing-loader-screen/publishing-loader-screen';
import type {PublishingEmbedInWebpagePanelProps, PublishingEmbedResponseProps} from './publishing-embed-in-webpage-panel.types';
import {MAX_LINK_LENGTH} from './publishing-embed-in-webpage-panel.types';
import styles from './publishing-embed-in-webpage-panel.module.scss';
import {useAppDispatch} from '@/hooks';
import {
  getDefaultOrderDetails,
  getDefaultPosterProductDataForProcessDesign,
  showErrorOnPanel,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {CopyLinkButton} from '@Components/copy-link-button/copy-link-button';

export function PublishingEmbedInWebpagePanel({displayType}: PublishingEmbedInWebpagePanelProps): ReactElement<any> {
  const hashedId = useHashedId();
  const [addLink, setAddLink] = useState(false);
  const [input, setInput] = useState('');
  const [oldInput, setOldInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [published, setPublished] = useState(false);
  const [htmlSnippet, setHtmlSnippet] = useState('');
  const [isLoadingForButtons, setIsLoadingForButtons] = useState(false);
  const [updateEmbedLink, setUpdateEmbedLink] = useState(false);
  const [linkEmbeded, setLinkEmbeded] = useState(false);
  const [invalidUrl, setInvalidUrl] = useState(false);
  const [changeHtmlSnippet, setChangeHtmlSnippet] = useState(false);

  const [showLoadScreen, setShowLoadScreen] = useState(true);
  const modalDetails = useContext(ModalDetails);
  const hasRun = useRef(false);
  const inMobile = modalDetails?.isMobileModal;
  const userLevel = window.PMW.getUserPremiumLevel();
  const freebieProductData = useFreebieProduct();
  const premiumProductDataForPrint = usePrintProduct();
  const showLoader = useShowLoader();
  const dispatch = useAppDispatch();
  const productUsed: PosterProductDataForProcessDesign = userLevel === USER_PREMIUM_LEVELS.PAYG ? freebieProductData : premiumProductDataForPrint;
  const isModalDisplay = displayType === DisplayType.MODAL;

  const orderId: string | null = productUsed?.orderId ?? null;
  const status = productUsed?.status ?? '';
  const productId: number = productUsed?.id ?? 0;

  const updateHTMLCleanedLink = (text: string): void => {
    if (!/^[a-zA-Z]+:\/\//.exec(input)) {
      setInput(`https://${text}`);
    }
  };
  const showEmbedPanelSuccess = (response: PublishingEmbedResponseProps): void => {
    setHtmlSnippet(response.htmlSnippet);
    setPublished(response.isPosterEnabled);
    if (response.hasUserAddedEmbedLink) {
      setAddLink(response.hasUserAddedEmbedLink);
      setUpdateEmbedLink(true);
      updateHTMLCleanedLink(response.customizableLink);
    }
  };

  const showEmbedPanel = (): void => {
    setShowLoadScreen(true);
    if (!showLoader && status === Status.DOWNLOADABLE) {
      void window.PMW.writeLocal('poster/showEmbedPanelNew', {hid: hashedId, pid: productId, orderID: orderId})
        .then((response: PublishingEmbedResponseProps) => {
          showEmbedPanelSuccess(response);
        })
        .catch(() => {
          showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
        })
        .finally(() => {
          setShowLoadScreen(false);
        });
    }
  };
  const initializeOrderProcess = (): void => {
    if (!hasRun.current) {
      dispatch(setShowLoader(true));
      dispatch(setOrderDetails(getDefaultOrderDetails()));
      if (userLevel === USER_PREMIUM_LEVELS.PAYG) {
        dispatch(setProductType(ProductType.FREEBIE));
      } else {
        dispatch(setProductType(ProductType.PRINT));
      }
      processDesignForPublishing(productUsed ?? getDefaultPosterProductDataForProcessDesign());
      hasRun.current = true;
    }
  };

  useEffect(() => {
    showEmbedPanel();
  }, [showLoader, status]);

  useEffect(() => {
    initializeOrderProcess();
  }, []);

  const handleEmbedPublishClick = (): void => {
    setIsLoadingForButtons(true);
    void window.PMW.writeLocal('poster/publishEmbedLinkNew', {hid: hashedId, orderID: orderId})
      .catch(() => {
        showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
      })
      .finally(() => {
        setIsLoadingForButtons(false);
        setPublished(true);
      });
  };

  const handleEmbedUnpublishClick = (): void => {
    setIsLoadingForButtons(true);
    void window.PMW.writeLocal('poster/unpublishEmbedLinkNew', {hid: hashedId, orderID: orderId})
      .catch(() => {
        showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
      })
      .finally(() => {
        setIsLoadingForButtons(false);
        setPublished(false);
        setCopied(false);
      });
  };

  const onRemoveEmbedLinkSucess = (response: PublishingEmbedResponseProps): void => {
    setHtmlSnippet(response.htmlSnippet);
    setInput('');
    setUpdateEmbedLink(false);
    setInvalidUrl(false);
    setAddLink(false);
    setLinkEmbeded(false);
  };
  const handleRemoveEmbedLink = (): void => {
    void window.PMW.writeLocal('poster/removeEmbedLinkNew', {hid: hashedId, orderID: orderId, useIframe: changeHtmlSnippet ? 0 : 1})
      .then((response: PublishingEmbedResponseProps) => {
        onRemoveEmbedLinkSucess(response);
      })
      .catch(() => {
        showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
      });
  };

  const onUpdateEmbedLinkSucess = (response: string): void => {
    setHtmlSnippet(response);
    setUpdateEmbedLink(true);
    setInvalidUrl(false);
    setLinkEmbeded(true);
  };

  const handleUpdateEmbedLink = (): void => {
    if (!window.PMW.util.isValidURLSimple(input) && input.length < MAX_LINK_LENGTH) {
      setInvalidUrl(true);
      return;
    }
    updateHTMLCleanedLink(input);

    void window.PMW.writeLocal('poster/updateLinkToEmbedNew', {hid: hashedId, val: input, useIframe: changeHtmlSnippet ? 0 : 1, orderID: orderId})
      .then((response: string) => {
        onUpdateEmbedLinkSucess(response);
      })
      .catch(() => {
        showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
      });
  };

  const handleSwapInnerHTML = (): void => {
    void window.PMW.writeLocal('poster/swapHtmlSnippetsNew', {hid: hashedId, orderID: orderId, useIframe: changeHtmlSnippet ? 1 : 0})
      .then((response: PublishingEmbedResponseProps) => {
        setHtmlSnippet(response.htmlSnippet);
      })
      .catch(() => {
        showErrorOnPanel(window.i18next.t('pmwjs_error_message'));
      })
      .finally(() => {
        setChangeHtmlSnippet(!changeHtmlSnippet);
        setCopied(false);
      });
  };

  const displaySimpleHTMLSnippet = (): ReactElement<any> => {
    return (
      <div className="flex-center">
        <Text
          val={changeHtmlSnippet ? window.i18next.t('pmwjs_webpage_embed_html_snippet_recommended') : window.i18next.t('pmwjs_webpage_embed_html_snippet_simple')}
          className={`${changeHtmlSnippet ? 'color-danger-100' : 'content-link'} body-xxs _cursor-pointer`}
          underline
          onClick={handleSwapInnerHTML}
        />
      </div>
    );
  };
  const getOverlayForHTMLLinks = (tooltipText: string): ReactElement<any> => {
    return (
      <OverlayTrigger
        trigger={['hover', 'focus', 'click']}
        overlay={<Tooltip className="spacing-m-b-2">{tooltipText}</Tooltip>}
        delay={{show: 200, hide: 200}}
        placement="top"
        popperConfig={undefined}
        show={undefined}
        defaultShow={undefined}
        onHide={undefined}
        onToggle={undefined}
        target={undefined}
        flip={undefined}
        offset={undefined}
      >
        <Icon icon="icon-info spacing-m-l-1" size={IconSize.SIZE_ICON_16} />
      </OverlayTrigger>
    );
  };
  const getHTMLSnippet = (): ReactElement<any> => {
    return (
      <div className={`flex-v-row ${styles.HTMLSnippetSpacing}`}>
        <div className="flexbox align-items-center content-body">
          <Text val={window.i18next.t('pmwjs_webpage_embed_html_snippet')} className="body-xs-bold content-body" />
          {changeHtmlSnippet ? <Text val={window.i18next.t('pmwjs_webpage_embed_simplified')} className="spacing-p-l-2" size={TextSize.XSMALL} /> : ''}
          {getOverlayForHTMLLinks(window.i18next.t('pmwjs_webpage_embed_info'))}
        </div>

        <InputField isDisabled inputClassName={published ? '' : 'content-sub-text'} hasIcon="icon-code" placeholder=" " input={htmlSnippet} />
        <div className="flexbox">
          <Text
            val={changeHtmlSnippet ? window.i18next.t('pmwjs_webpage_embed_link_wont_work') : window.i18next.t('pmwjs_webpage_embed_trouble')}
            className={`${changeHtmlSnippet ? 'color-danger-100' : 'content-sub-text'} body-xxs spacing-p-r-1`}
          />
          {displaySimpleHTMLSnippet()}
        </div>
      </div>
    );
  };

  const getCopyButton = (): ReactElement<any> => {
    return (
      <CopyLinkButton
        linkToCopy={htmlSnippet}
        tooltipText={window.i18next.t('pmwjs_code_copied')}
        buttonProps={{
          icon: copied ? 'icon-check' : 'icon-copy',
          type: copied ? Type.SUCCESS : Type.PRIMARY,
          text: copied ? window.i18next.t('pmwjs_insights_keywords_copied') : window.i18next.t('pmwjs_publish_copy_code'),
          size: Size.SMALL,
          disabled: !published,
          onClick: () => {
            setCopied(true);
          },
          isActive: false,
        }}
        onCopyFeedbackComplete={() => {
          setCopied(false);
        }}
      />
    );
  };
  const getPublishButtons = (): ReactElement<any> => {
    return (
      <Button
        type={Type.SECONDARY}
        size={Size.SMALL}
        iconPlacement={IconPlacement.LEFT}
        text={published ? window.i18next.t('pmwjs_webpage_embed_disabled') : window.i18next.t('pmwjs_webpage_embed_enable')}
        iconClassName={published ? 'content-danger' : ''}
        textClasses={published ? 'content-disabled-dark' : ''}
        icon={published ? 'icon-ban' : 'icon-check'}
        onClick={published ? handleEmbedUnpublishClick : handleEmbedPublishClick}
        isLoading={isLoadingForButtons}
      />
    );
  };

  const getButtonsForEmbedOptions = (): ReactElement<any> => {
    return (
      <div className={`flex-row-justify-between spacing-m-t-4`}>
        {getPublishButtons()}
        {getCopyButton()}
      </div>
    );
  };

  const getInputFieldCustomControls = (): InputFieldControlItemData[] => {
    return updateEmbedLink
      ? [
          {
            id: 'icon-pencil-line',
            type: InputControlItemType.ICON_BTNS,
            icon: 'icon-pencil-line',
            className: 'content-body size-icon-20',
          },
          {
            id: 'icon-delete',
            type: InputControlItemType.ICON_BTNS,
            icon: 'icon-delete',
            className: 'content-danger size-icon-20',
          },
        ]
      : [
          {
            id: 'icon-check',
            type: InputControlItemType.ICON_BTNS,
            icon: 'icon-check',
            className: 'content-success size-icon-20',
          },
          {
            id: 'icon-close',
            type: InputControlItemType.ICON_BTNS,
            icon: 'icon-close',
            className: 'content-danger size-icon-20',
          },
        ];
  };

  const inputFieldCustomControlsClickHandler = (iconName: string): void => {
    if (iconName === 'icon-close') {
      if (linkEmbeded) {
        setUpdateEmbedLink(true);
        setInput(oldInput);
      } else {
        setAddLink(false);
      }
      setInvalidUrl(false);
    } else if (iconName === 'icon-check') {
      handleUpdateEmbedLink();
    } else if (iconName === 'icon-pencil-line') {
      setOldInput(input);
      setUpdateEmbedLink(false);
      setInvalidUrl(false);
    } else if (iconName === 'icon-delete') {
      handleRemoveEmbedLink();
    }
  };
  const addLinkButton = (): ReactElement<any> => {
    return !addLink ? (
      <Button
        onClick={() => {
          setAddLink(true);
          setInput('');
        }}
        type={Type.DASHED}
        size={Size.SMALL}
        iconPlacement={IconPlacement.LEFT}
        iconClassName="icon"
        text={window.i18next.t('pmwjs_add_embed_link')}
        icon="icon-plus"
        isFullWidth
        customClasses="spacing-m-t-1"
      />
    ) : (
      <InputField
        inputType={InputFieldType.TEXT}
        hasIcon="icon-link"
        useEmptyPlaceholder
        className="spacing-m-t-1"
        inputClassName="content-sub-text"
        iconClassName="content-body"
        input={input}
        isReadOnly={updateEmbedLink}
        onInputChange={(updatedInput: string): void => {
          setInput(updatedInput);
        }}
        controls={{
          showDefaultControls: false,
          showSingleDefaultControlOnly: false,
          customControls: getInputFieldCustomControls(),

          onClick(iconName: string) {
            inputFieldCustomControlsClickHandler(iconName);
          },
        }}
      />
    );
  };
  const handleLinkAddition = (): ReactElement<any> => {
    return (
      <div className="flex-v-row">
        <div className="flexbox align-items-center content-body">
          <Text val={window.i18next.t('pmwjs_webpage_embed_link')} className="body-xs-bold content-body" />
          {getOverlayForHTMLLinks(window.i18next.t('pmwjs_webpage_embed_link_info'))}
        </div>
        {addLinkButton()}
      </div>
    );
  };

  const getPanelStyles = (): string => {
    if (inMobile) {
      return 'flex-v-row spacing-p-4';
    } else {
      return `flex-v-row ${isModalDisplay ? 'spacing-p-t-8 spacing-p-b-8 spacing-p-r-12 spacing-p-l-12' : 'spacing-p-t-6'}`;
    }
  };

  const getInvalidUrlText = (): ReactElement<any> | null => {
    return invalidUrl ? (
      <div className="spacing-p-t-1">
        <Text className="color-danger-100" size={TextSize.XSMALL} val={window.i18next.t('pmwjs_enter_valid_url')} />
      </div>
    ) : null;
  };
  const getPanelContent = (): ReactElement<any> => {
    return showLoadScreen ? (
      <PublishingLoaderScreen customHeight={isModalDisplay ? styles.customModalHeight : styles.customDropDownHeight} loaderSize={LOADER_SIZE.LARGE} />
    ) : (
      <>
        {getHTMLSnippet()}
        {getButtonsForEmbedOptions()}
        {published ? (
          <div className={!isModalDisplay || inMobile ? 'spacing-m-t-6' : 'spacing-m-t-8'}>
            {handleLinkAddition()}
            {getInvalidUrlText()}
          </div>
        ) : null}
      </>
    );
  };

  return <div className={showLoader ? '' : getPanelStyles()}>{showLoader ? <PublishingProgressBar /> : getPanelContent()}</div>;
}
